import { GridColumnId, GridElementId, GridRowId } from "../idTypeBrands.js";

import { GridElement, GridRow } from "./gridTypes.js";

export interface GridElementLocator {
  rowId: GridRowId;
  columnId: GridColumnId;
  elementId: GridElementId;
}

export interface GridElementLocationData<T extends GridRow> {
  rowIdx: number;
  row: T;
  columnIdx: number;
  column: T["gridColumns"][number];
  elementIdx: number;
  element: T["gridColumns"][number]["gridElements"][number];
}

/** Given a `GridElementLocator`, return detailed information about the elements location and any ancestors */
// (Making this generic means that it optionally work well with `WritableDraft<GridRow>` types from immer)
export const getGridElement = <T extends GridRow>(
  gridRows: readonly T[],
  { columnId, elementId, rowId }: GridElementLocator,
): GridElementLocationData<T> | undefined => {
  const rowIdx = gridRows.findIndex((r) => r.id === rowId);
  if (rowIdx < 0) {
    return undefined;
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- above findIndex guarantees this exists
  const row = gridRows[rowIdx]!;

  const columnIdx = row.gridColumns.findIndex((c) => c.id === columnId);
  if (columnIdx < 0) {
    return undefined;
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- above findIndex guarantees this exists
  const column = row.gridColumns[columnIdx]!;

  const elementIdx = column.gridElements.findIndex((e) => e.id === elementId);
  if (elementIdx < 0) {
    return undefined;
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- above findIndex guarantees this exists
  const element = column.gridElements[elementIdx]!;

  return {
    rowIdx,
    row,
    columnIdx,
    column,
    elementIdx,
    element,
  };
};

/** Returns a `GridElementLocator` for all `GridElement`s matching `condition` */
export const findGridElements = (
  gridRows: readonly GridRow[],
  condition: (ele: GridElement) => boolean,
): GridElementLocator[] => {
  const locators: GridElementLocator[] = [];
  gridRows.forEach((row) => {
    row.gridColumns.forEach((col) => {
      col.gridElements.forEach((ele) => {
        if (condition(ele)) {
          locators.push({
            rowId: row.id,
            columnId: col.id,
            elementId: ele.id,
          });
        }
      });
    });
  });
  return locators;
};
